import React, { useState, useEffect } from 'react';
import ProvincesService from '../services/ProvincesService';

// Helper function to normalize text for comparison
const normalizeText = (text) => {
    if (!text) return '';
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
        .replace(/[^\w\s]/g, '') // Remove special characters
        .trim();
};

// Helper function to find item by flexible name matching
const findByName = (items, searchName) => {
    if (!searchName || !items || items.length === 0) return null;
    
    const normalizedSearch = normalizeText(searchName);
    
    // First try exact match after normalization
    let found = items.find(item => normalizeText(item.name) === normalizedSearch);
    
    // If not found, try includes match
    if (!found) {
        found = items.find(item => {
            const normalizedItemName = normalizeText(item.name);
            return normalizedItemName.includes(normalizedSearch) || normalizedSearch.includes(normalizedItemName);
        });
    }
    
    return found;
};

const AddressForm = ({ onSubmit, onCancel, isVisible, initialData = null, userInfo = null }) => {
    const [formData, setFormData] = useState({
        fullName: '',
        province: '',
        district: '',
        ward: '',
        streetAddress: '',
        phone: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    
    // State for dynamic data loading
    const [provinces, setProvinces] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [wards, setWards] = useState([]);
    const [loadingProvinces, setLoadingProvinces] = useState(false);
    const [loadingDistricts, setLoadingDistricts] = useState(false);
    const [loadingWards, setLoadingWards] = useState(false);

    // Load provinces when component mounts
    useEffect(() => {
        const loadProvinces = async () => {
            setLoadingProvinces(true);
            try {
                const provincesData = await ProvincesService.getProvinces();
                // Sort provinces alphabetically by name
                const sortedProvinces = provincesData.sort((a, b) => a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' }));
                setProvinces(sortedProvinces);
            } catch (error) {
                console.error('Error loading provinces:', error);
                // Fallback to hardcoded list if API fails
                const fallbackProvinces = [
                    'Hà Nội', 'TP. Hồ Chí Minh', 'Đà Nẵng', 'Hải Phòng', 'Cần Thơ',
                    'An Giang', 'Bà Rịa - Vũng Tàu', 'Bắc Giang', 'Bắc Kạn', 'Bạc Liêu',
                    'Bắc Ninh', 'Bến Tre', 'Bình Định', 'Bình Dương', 'Bình Phước',
                    'Bình Thuận', 'Cà Mau', 'Cao Bằng', 'Đắk Lắk', 'Đắk Nông',
                    'Điện Biên', 'Đồng Nai', 'Đồng Tháp', 'Gia Lai', 'Hà Giang',
                    'Hà Nam', 'Hà Tĩnh', 'Hải Dương', 'Hậu Giang', 'Hòa Bình',
                    'Hưng Yên', 'Khánh Hòa', 'Kiên Giang', 'Kon Tum', 'Lai Châu',
                    'Lâm Đồng', 'Lạng Sơn', 'Lào Cai', 'Long An', 'Nam Định',
                    'Nghệ An', 'Ninh Bình', 'Ninh Thuận', 'Phú Thọ', 'Phú Yên',
                    'Quảng Bình', 'Quảng Nam', 'Quảng Ngãi', 'Quảng Ninh', 'Quảng Trị',
                    'Sóc Trăng', 'Sơn La', 'Tây Ninh', 'Thái Bình', 'Thái Nguyên',
                    'Thanh Hóa', 'Thừa Thiên Huế', 'Tiền Giang', 'Trà Vinh', 'Tuyên Quang',
                    'Vĩnh Long', 'Vĩnh Phúc', 'Yên Bái'
                ].map(name => ({ name, code: null }));
                // Sort fallback provinces alphabetically by name
                const sortedFallbackProvinces = fallbackProvinces.sort((a, b) => a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' }));
                setProvinces(sortedFallbackProvinces);
            } finally {
                setLoadingProvinces(false);
            }
        };

        if (isVisible) {
            loadProvinces();
        }
    }, [isVisible]);

    // Load initial data when form becomes visible
    useEffect(() => {
        if (isVisible && initialData) {
            setFormData({
                fullName: initialData.fullName || '',
                province: initialData.province || '',
                district: initialData.district || '',
                ward: initialData.ward || '',
                streetAddress: initialData.streetAddress || '',
                phone: initialData.phone || userInfo?.phoneNumber || ''
            });
        } else if (isVisible && !initialData) {
            // Reset form when opening for new address
            setFormData({
                fullName: '',
                province: '',
                district: '',
                ward: '',
                streetAddress: '',
                phone: userInfo?.phoneNumber || ''
            });
            setDistricts([]);
            setWards([]);
        }
    }, [isVisible, initialData, userInfo]);

    // Load districts and wards when provinces are loaded and we have initial data
    useEffect(() => {
        const loadInitialLocationData = async () => {
            if (!isVisible || !initialData || !provinces.length) return;
            
            const { province, district } = initialData;
            
            // Load districts if we have province
            if (province) {
                console.log('Loading districts for initial province:', province);
                const selectedProvince = findByName(provinces, province);
                console.log('Found province:', selectedProvince);
                
                if (selectedProvince && selectedProvince.code) {
                    setLoadingDistricts(true);
                    try {
                        const districtsData = await ProvincesService.getDistrictsByProvince(selectedProvince.code);
                        // Sort districts alphabetically by name
                        const sortedDistricts = districtsData.sort((a, b) => a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' }));
                        setDistricts(sortedDistricts);
                        console.log('Loaded districts:', sortedDistricts);
                        
                        // Load wards if we have district
                        if (district && sortedDistricts.length > 0) {
                            console.log('Loading wards for initial district:', district);
                            const selectedDistrict = findByName(sortedDistricts, district);
                            console.log('Found district:', selectedDistrict);
                            
                            if (selectedDistrict && selectedDistrict.code) {
                                setLoadingWards(true);
                                try {
                                    const wardsData = await ProvincesService.getWardsByDistrict(selectedDistrict.code);
                                    // Sort wards alphabetically by name
                                    const sortedWards = wardsData.sort((a, b) => a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' }));
                                    setWards(sortedWards);
                                    console.log('Loaded wards:', sortedWards);
                                } catch (error) {
                                    console.error('Error loading initial wards:', error);
                                    setWards([]);
                                } finally {
                                    setLoadingWards(false);
                                }
                            }
                        }
                    } catch (error) {
                        console.error('Error loading initial districts:', error);
                        setDistricts([]);
                    } finally {
                        setLoadingDistricts(false);
                    }
                }
            }
        };

        loadInitialLocationData();
    }, [isVisible, initialData, provinces]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.fullName.trim()) {
            newErrors.fullName = 'Vui lòng nhập họ và tên';
        }

        if (!formData.province.trim()) {
            newErrors.province = 'Vui lòng chọn tỉnh/thành phố';
        }

        if (!formData.district.trim()) {
            newErrors.district = 'Vui lòng chọn quận/huyện';
        }

        if (!formData.ward.trim()) {
            newErrors.ward = 'Vui lòng chọn phường/xã';
        }

        if (!formData.streetAddress.trim()) {
            newErrors.streetAddress = 'Vui lòng nhập địa chỉ chi tiết';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Vui lòng nhập số điện thoại nhận quà';
        } else if (formData.phone.trim().length < 10) {
            newErrors.phone = 'Số điện thoại phải có ít nhất 10 số';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = async (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }

        // Handle cascade loading for province/district/ward
        if (field === 'province') {
            // Clear district and ward when province changes
            setFormData(prev => ({
                ...prev,
                district: '',
                ward: ''
            }));
            setDistricts([]);
            setWards([]);

            // Load districts for selected province
            if (value) {
                console.log('value: ', value);
                console.log('provinces: ', provinces);
                const selectedProvince = findByName(provinces, value);
                console.log('selectedProvince: ', selectedProvince);
                if (selectedProvince && selectedProvince.code) {
                    setLoadingDistricts(true);
                    try {
                        const districtsData = await ProvincesService.getDistrictsByProvince(selectedProvince.code);
                        // Sort districts alphabetically by name
                        const sortedDistricts = districtsData.sort((a, b) => a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' }));
                        setDistricts(sortedDistricts);
                    } catch (error) {
                        console.error('Error loading districts:', error);
                        setDistricts([]);
                    } finally {
                        setLoadingDistricts(false);
                    }
                }
            }
        } else if (field === 'district') {
            // Clear ward when district changes
            setFormData(prev => ({
                ...prev,
                ward: ''
            }));
            setWards([]);

            // Load wards for selected district
            if (value) {
                const selectedDistrict = findByName(districts, value);
                if (selectedDistrict && selectedDistrict.code) {
                    setLoadingWards(true);
                    try {
                        const wardsData = await ProvincesService.getWardsByDistrict(selectedDistrict.code);
                        // Sort wards alphabetically by name
                        const sortedWards = wardsData.sort((a, b) => a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' }));
                        setWards(sortedWards);
                    } catch (error) {
                        console.error('Error loading wards:', error);
                        setWards([]);
                    } finally {
                        setLoadingWards(false);
                    }
                }
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            console.log('Submitting address form data:', formData);
            await onSubmit(formData);
        } catch (error) {
            console.error('Error submitting address:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    {/* Header */}
                    <div className="text-center mb-6">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#f0f9f4' }}>
                            <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="24" 
                                height="24" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                style={{ color: '#277f4d' }}
                            >
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                <circle cx="12" cy="10" r="3"></circle>
                            </svg>
                        </div>
                        <h2 className="text-xl font-bold text-gray-800">Nhập địa chỉ nhận quà</h2>
                        <p className="text-gray-600 mt-1">Chúc mừng! Bạn đã nhập mã thành công 10 sản phẩm. Vui lòng nhập địa chỉ chính xác để nhận quà.</p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Tỉnh/Thành phố */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Tỉnh/Thành phố *
                            </label>
                            <select
                                value={formData.province}
                                onChange={(e) => handleInputChange('province', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                                    errors.province ? 'border-red-500' : 'border-gray-300'
                                }`}
                                style={{ '--tw-ring-color': '#277f4d' }}
                                disabled={loadingProvinces}
                            >
                                <option value="">
                                    {loadingProvinces ? 'Đang tải...' : 'Chọn tỉnh/thành phố'}
                                </option>
                                {provinces.map((province) => (
                                    <option key={province.code || province.name} value={province.name}>
                                        {province.name}
                                    </option>
                                ))}
                            </select>
                            {errors.province && (
                                <p className="text-red-500 text-xs mt-1">{errors.province}</p>
                            )}
                        </div>

                        {/* Quận/Huyện */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Quận/Huyện *
                            </label>
                            <select
                                value={formData.district}
                                onChange={(e) => handleInputChange('district', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                                    errors.district ? 'border-red-500' : 'border-gray-300'
                                }`}
                                style={{ '--tw-ring-color': '#277f4d' }}
                                disabled={!formData.province || loadingDistricts}
                            >
                                <option value="">
                                    {loadingDistricts ? 'Đang tải...' : !formData.province ? 'Vui lòng chọn tỉnh/thành phố trước' : 'Chọn quận/huyện'}
                                </option>
                                {districts.map((district) => (
                                    <option key={district.code} value={district.name}>
                                        {district.name}
                                    </option>
                                ))}
                            </select>
                            {errors.district && (
                                <p className="text-red-500 text-xs mt-1">{errors.district}</p>
                            )}
                        </div>

                        {/* Phường/Xã */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Phường/Xã *
                            </label>
                            <select
                                value={formData.ward}
                                onChange={(e) => handleInputChange('ward', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                                    errors.ward ? 'border-red-500' : 'border-gray-300'
                                }`}
                                style={{ '--tw-ring-color': '#277f4d' }}
                                disabled={!formData.district || loadingWards}
                            >
                                <option value="">
                                    {loadingWards ? 'Đang tải...' : !formData.district ? 'Vui lòng chọn quận/huyện trước' : 'Chọn phường/xã'}
                                </option>
                                {wards.map((ward) => (
                                    <option key={ward.code} value={ward.name}>
                                        {ward.name}
                                    </option>
                                ))}
                            </select>
                            {errors.ward && (
                                <p className="text-red-500 text-xs mt-1">{errors.ward}</p>
                            )}
                        </div>


                        {/* Địa chỉ chi tiết */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Địa chỉ chi tiết *
                            </label>
                            <input
                                type="text"
                                value={formData.streetAddress}
                                onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                                placeholder="Ví dụ: 123 Đường Nguyễn Huệ, Quận 1"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                                    errors.streetAddress ? 'border-red-500' : 'border-gray-300'
                                }`}
                                style={{ '--tw-ring-color': '#277f4d' }}
                            />
                            {errors.streetAddress && (
                                <p className="text-red-500 text-xs mt-1">{errors.streetAddress}</p>
                            )}
                        </div>

                        {/* Họ và tên */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Họ và tên người nhận *
                            </label>
                            <input
                                type="text"
                                value={formData.fullName}
                                onChange={(e) => handleInputChange('fullName', e.target.value)}
                                placeholder="Ví dụ: Nguyễn Văn A"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                                    errors.fullName ? 'border-red-500' : 'border-gray-300'
                                }`}
                                style={{ '--tw-ring-color': '#277f4d' }}
                            />
                            {errors.fullName && (
                                <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>
                            )}
                        </div>

                        {/* Số điện thoại nhận quà */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Số điện thoại nhận quà *
                            </label>
                            <input
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                placeholder="Ví dụ: 0123456789"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                                    errors.phone ? 'border-red-500' : 'border-gray-300'
                                }`}
                                style={{ '--tw-ring-color': '#277f4d' }}
                            />
                            {errors.phone && (
                                <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                            )}
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-3 pt-4">
                            <button
                                type="button"
                                onClick={onCancel}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                disabled={loading}
                            >
                                Hủy
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: '#277f4d' }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#1f5f3a'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#277f4d'}
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                        Đang lưu...
                                    </div>
                                ) : (
                                    'Lưu địa chỉ'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddressForm;
